import { ChronoUnit, LocalTime } from '@js-joda/core';
import { useMemo } from 'react';
import styled from 'styled-components';

import { assertUnreachable, useTranslate } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { personnelTranslationKeys, timeSlotsTranslationKeys } from '../../../../../translations';
import { AvailabilityFormController } from '../../../common.types';
import { TimeSlotBean } from '../AvailabilityTimeSlot.types';

import {
   MAX_FIXED_DURATION,
   PredefinedTimeSlot,
   TIME_SLOT_NONE,
   TIME_SLOT_OTHER,
} from './AvailabilityTimeSlotFixed.types';

const StyledAvailabilityTimeSlotSelect = styled(Select)`
   width: 285px;
` as typeof Select;

/* eslint-disable @typescript-eslint/no-magic-numbers */
export const timeSlots: TimeSlotBean[] = [TIME_SLOT_NONE, 10, 15, 20, MAX_FIXED_DURATION, TIME_SLOT_OTHER];

const getLabel = (timeSlot: TimeSlotBean): string => {
   switch (timeSlot) {
      case TIME_SLOT_NONE:
         return timeSlotsTranslationKeys.withoutDivision;
      case 10:
         return timeSlotsTranslationKeys.tenMinutes;
      case 15:
         return timeSlotsTranslationKeys.fifteenMinutes;
      case 20:
         return timeSlotsTranslationKeys.twentyMinutes;
      case MAX_FIXED_DURATION:
         return timeSlotsTranslationKeys.thirtyMinutes;
      case TIME_SLOT_OTHER:
         return timeSlotsTranslationKeys.other;
      default:
         return assertUnreachable(timeSlot);
   }
};

type AvailabilityTimeSlotSelectProps = {
   readonly register: AvailabilityFormController['register'];
};

type UseValidationRulesReturn = {
   validate: (value: unknown, getValue: (fieldName: string) => unknown) => string | undefined;
};

const isNumericalTimeSlot = (timeSlot: TimeSlotBean): timeSlot is PredefinedTimeSlot => typeof timeSlot === 'number';
const getPredefinedTimeSlots = (): PredefinedTimeSlot[] => timeSlots.filter(isNumericalTimeSlot);
const isSlotDurationPredefined = (slotDuration: TimeSlotBean | number): slotDuration is PredefinedTimeSlot => {
   const predefinedTimeSlots = getPredefinedTimeSlots();
   return predefinedTimeSlots.some((predefinedTimeSlot) => predefinedTimeSlot === slotDuration);
};

const useValidationRules = (): UseValidationRulesReturn => {
   return useMemo(
      () => ({
         required: true,
         validate: (data, getValue) => {
            const slotDurationFixed = data as TimeSlotBean;
            const sendToDocplanner = getValue('sendToDocplanner');
            const sendToPatientPortal = getValue('sendToPatientPortal');
            const withoutSlotDivision = slotDurationFixed === 'none';

            if ((sendToDocplanner || sendToPatientPortal) && withoutSlotDivision) {
               return personnelTranslationKeys.eRegistrationRequiresSlotDivision;
            }

            if (slotDurationFixed === TIME_SLOT_OTHER || slotDurationFixed === TIME_SLOT_NONE) {
               return undefined;
            }

            const startTime = getValue('startTime') as LocalTime | null;
            const endTime = getValue('endTime') as LocalTime | null;

            if (!startTime || !endTime) {
               return undefined;
            }

            const diff = startTime.until(endTime, ChronoUnit.MINUTES);
            if (diff < slotDurationFixed) {
               return personnelTranslationKeys.availabilityShorterThanVisitTime;
            }
            return undefined;
         },
      }),
      []
   );
};

export const getDefaultSlotDurationFixed = (slotDurationManual: number | null): TimeSlotBean => {
   if (slotDurationManual === null) {
      return TIME_SLOT_NONE;
   }

   if (isSlotDurationPredefined(slotDurationManual)) {
      return slotDurationManual;
   }

   return TIME_SLOT_OTHER;
};

const useTriggerLabelExtractor = (): ((item: TimeSlotBean) => string) => {
   const translate = useTranslate();
   return (item) => translate(getLabel(item));
};

export const AvailabilityTimeSlotFixed = (props: AvailabilityTimeSlotSelectProps): JSX.Element => {
   return (
      <StyledAvailabilityTimeSlotSelect<TimeSlotBean>
         label={personnelTranslationKeys.visitDuration}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         testId={'slotDuration'}
         controller={props.register('slotDurationFixed', useValidationRules())}
      >
         <Select.List items={timeSlots}>
            {(item: TimeSlotBean) => <Select.Item item={item} label={getLabel(item)} testId={item} />}
         </Select.List>
      </StyledAvailabilityTimeSlotSelect>
   );
};
